/**
 * dev/fillForm.ts
 *
 * Installs a console helper that fills the oneform patient intake + policy
 * forms with valid sample data for quick manual testing.
 *
 * Usage: open the browser console while on the form and run `fillForm()`.
 * Pass an object to override any patientForm fields, e.g. `fillForm({ name: "Bob" })`.
 *
 * type-r model writes don't flow back into Vue's render, so filling then
 * reloading is how the values reach the visible inputs: the helper writes the
 * data, flushes it straight to IndexedDB, then reloads so the normal
 * restore-backup path rehydrates the UI reactively. Call `fillForm(over, false)`
 * to skip the reload (data still saved; UI won't refresh until you reload).
 */

import type OneformStore from '#stores/oneform'

/**
 * Draw a realistic-looking cursive signature on a transparent canvas and return
 * it as a PNG data URL. Black strokes so it matches the signature pad's pen.
 * `seed` lets each pad get a slightly different-looking mark.
 */
function makeSignature(seed = 1): string {
    const w = 600
    const h = 200
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!

    ctx.strokeStyle = '#101010'
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'

    // Simple seeded pseudo-random so the scribble is stable but varies per pad.
    let s = seed * 9301 + 49297
    const rnd = () => {
        s = (s * 9301 + 49297) % 233280
        return s / 233280
    }
    const jitter = (amt: number) => (rnd() - 0.5) * amt

    const baseY = h * 0.58

    // Main flowing cursive stroke: a leading capital loop followed by a run of
    // connected humps, tapering off into a trailing flourish.
    ctx.lineWidth = 2.6
    ctx.beginPath()
    ctx.moveTo(40, baseY + 10)
    // capital-letter loop
    ctx.bezierCurveTo(30, baseY - 70, 95, baseY - 90, 100, baseY - 20)
    ctx.bezierCurveTo(104, baseY + 30, 70, baseY + 35, 90, baseY - 5)
    // connected middle humps
    let x = 110
    for (let i = 0; i < 6; i++) {
        const up = baseY - 45 + jitter(18)
        const dn = baseY + 20 + jitter(14)
        const nx = x + 55 + jitter(18)
        ctx.bezierCurveTo(x + 12, up, x + 40, up, nx * 0.5 + x * 0.5, baseY + jitter(8))
        ctx.bezierCurveTo(nx - 30, dn, nx - 8, dn, nx, baseY - 8 + jitter(10))
        x = nx
    }
    // trailing flourish
    ctx.bezierCurveTo(x + 40, baseY - 60, x + 90, baseY + 10, x + 150, baseY - 4)
    ctx.stroke()

    // Long underline swoop beneath the name.
    ctx.lineWidth = 1.8
    ctx.beginPath()
    ctx.moveTo(45, baseY + 42)
    ctx.bezierCurveTo(x * 0.5, baseY + 70, x, baseY + 20, x + 150, baseY + 40)
    ctx.stroke()

    // A dot (over an "i") for extra realism.
    ctx.beginPath()
    ctx.arc(220 + jitter(30), baseY - 70, 2.2, 0, Math.PI * 2)
    ctx.fillStyle = '#101010'
    ctx.fill()

    return canvas.toDataURL('image/png')
}

export function installFillForm(store: OneformStore) {
    ;(window as any).fillForm = async (overrides: Record<string, any> = {}, reload = true) => {
        // --- Patient info form ---------------------------------------------
        const patientSig = makeSignature(1)
        store.patientForm.set({
            name: 'Jane Q. Sample',
            dateOfBirth: '1970-05-15',
            mailingAddress: {
                address: '123 Test Street',
                city: 'Charlottesville',
                state: 'VA',
                zip: '22903',
            },
            cellPhone: '5555551234',
            homePhone: '5555555678',
            email: 'jane.sample@example.com',
            primaryCareDoctor: 'Dr. Primary Care',
            referringDoctor: 'Dr. Referring Physician',
            preferredPharmacy: 'CVS Pharmacy',
            preferredPharmacyLocation: 'Main St, Charlottesville, VA',
            currentlyWear: 'Glasses',
            eyeMedications: false,
            height: `5' 6"`,
            weight: '150',

            // Cardiac / medical history yes-no answers — all "no" (healthy)
            coronaryArteryDisease: false,
            congenitalHeartDisease: false,
            rheumatoidHeartDisease: false,
            respiratoryDisease: false,
            highBloodPressure: false,
            sleepApnea: false,
            anesthesiaDifficulty: false,
            bleedingDisorder: false,
            liverDisease: false,
            kidneyDisease: false,
            dialysis: false,
            diabetes: false,
            thyroidDisease: false,
            cancer: false,
            bigbmi: false,

            // Allergies + medications (new meds section)
            medicationAllergies: false,
            latexAllergy: false,
            medicationListUploadType: 'text',
            medicationListText: 'None',

            // Review-of-symptoms checkboxes (all unchecked = no problems)
            heart: false,
            respiratory: false,
            earNoseThroat: false,
            gastrointestinal: false,
            urinary: false,
            hematologicLymphatic: false,
            endocrine: false,
            integumentary: false,
            musculoskeletal: false,
            neurological: false,
            psychiatric: false,
            allergicImmunologic: false,

            // Lifestyle
            alcohol: 'Never',
            tobacco: 'Never',

            signature: patientSig,
            ...overrides,
        })

        // Nested incident sub-records — mark each as answered "no"
        const noIncident = ['heartAttack', 'angina', 'chestPain', 'irregularHeartRate',
            'palpitations', 'murmur', 'stroke', 'pacemaker', 'aicd']
        for (const key of noIncident) store.patientForm[key].set({ confirmed: false })

        const noTypeIncident = ['rightEye', 'leftEye', 'heartSurgery']
        for (const key of noTypeIncident) store.patientForm[key].set({ confirmed: false })

        // --- Authorization & disclosure form -------------------------------
        store.authorizationDisclosureForm.set({
            provider: 'Blue Cross Blue Shield',
            memberId: 'ABC123456789',
            subscriberName: 'Jane Q. Sample',
            subscriberDob: '1970-05-15',
            signature: makeSignature(2),
        })

        // --- Cancellation + financial policy forms -------------------------
        store.cancellationPolicyForm.set({ signature: makeSignature(3) })
        store.financialPolicyForm.set({ signature: makeSignature(4) })

        // Flush straight to IndexedDB (bypass the 3s debounce), then reload so
        // the restore-backup path rehydrates the inputs through Vue's reactivity.
        await store.autosaveData()
        console.log(
            '%c✔ Forms filled with sample data.',
            'color:#2e7d32;font-weight:bold',
        )
        if (reload) {
            console.log('Reloading to render values — accept the "restore progress" prompt.')
            location.reload()
        }
    }
}
